import {
  Alert,
  Box,
  createTheme,
  CssBaseline,
  Divider,
  Paper,
  ThemeProvider,
  Typography,
} from "@mui/material";
import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useCurrentProfile } from "../../ContextWrapper";
import { PrimaryButton } from "../designSystem";
import PlatformLayout from "../dashboard/dashboardLayout";
import ClientDashboardLayout from "./ClientDashboardLayout";

const theme = createTheme({
  typography: {
    fontFamily: "'Rethink Sans', 'Inter', sans-serif",
  },
  palette: {
    background: {
      default: "#F4F6FF",
    },
    text: {
      primary: "#111827",
      secondary: "#6B7280",
    },
    primary: {
      main: "#536DEC",
      dark: "#4C63D7",
    },
  },
});

const LayoutWrapper = ({ children, useSaasLayout }) => {
  if (!useSaasLayout) {
    return (
      <PlatformLayout header={true}>
        <Box sx={{ flexGrow: 1, p: 3, width: "100%", minWidth: 0 }}>
          {children}
        </Box>
      </PlatformLayout>
    );
  }

  return (
    <ClientDashboardLayout contentMaxWidth="100%">
      {children}
    </ClientDashboardLayout>
  );
};

export default function FeaturePreview({
  title,
  description,
  imagePath,
  isSaasExperience = true,
}) {
  const navigate = useNavigate();
  const params = useParams();
  const { profile } = useCurrentProfile();
  const storedCustomerId = useMemo(() => {
    try {
      return sessionStorage.getItem("selectedCustomerId");
    } catch (error) {
      return null;
    }
  });

  const { true_customer_id } = useMemo(() => {
    const customers = Array.isArray(profile?.customers)
      ? profile?.customers
      : [];
    const isStaff = profile?.type === "admin" || profile?.type === "accountant";
    const routeId = params?.customer_id ? String(params.customer_id) : null;

    if (isStaff) {
      const staffCustomerId =
        routeId || storedCustomerId || customers[0]?.id?.toString() || null;
      return {
        customer_id: staffCustomerId,
        true_customer_id: staffCustomerId,
        isDemoCustomer: false,
      };
    }

    if (!customers.length) {
      return {
        customer_id: null,
        true_customer_id: null,
        isDemoCustomer: true,
      };
    }

    const nonDemoCustomers = customers.filter((c) => !c?.is_demo);

    const trueId =
      storedCustomerId || nonDemoCustomers[0]?.id || customers[0]?.id || null;

    return {
      true_customer_id: trueId,
    };
  }, [profile, storedCustomerId, params?.customer_id]);

  const currentCustomer = useMemo(() => {
    const customers = Array.isArray(profile?.customers)
      ? profile.customers
      : [];

    return customers.find((c) => String(c.id) === String(true_customer_id));
  }, [profile?.customers, true_customer_id]);

  const hasBookedCallStep =
    currentCustomer?.completed_setup_steps?.includes("book_a_call");

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <LayoutWrapper useSaasLayout={isSaasExperience}>
        <Box
          sx={{
            height: "100%",
            width: "100%",
            p: 0,
            mx: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography
              sx={{
                fontSize: "24px",
                fontWeight: 600,
                fontFamily: "Rethink Sans",
              }}
            >
              {title}
            </Typography>
          </Box>

          <Divider
            sx={{
              my: 2,
            }}
          />
          <Alert
            severity="info"
            icon={
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  backgroundColor: "#0288D11A",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Box
                  component="img"
                  src="/infoIcon.svg"
                  sx={{
                    width: 20,
                    height: 20,
                  }}
                />
              </Box>
            }
            sx={{
              borderRadius: "8px",
              width: "100%",
              mb: 4,
              alignItems: "center",
              backgroundColor: "rgba(2, 136, 209, 0.08)",
              border: "1px solid rgba(2, 136, 209, 0.12)",

              "& .MuiAlert-icon": {
                alignItems: "center",
                display: "flex",
                mt: 0,
              },
            }}
          >
            <Typography
              fontWeight={600}
              sx={{ color: "#0288D1", fontFamily: "Rethink Sans" }}
            >
              What is {title}?
            </Typography>

            <Typography
              sx={{ color: "rgba(0,0,0,0.6)", fontFamily: "Rethink Sans" }}
            >
              {description}
            </Typography>
          </Alert>

          <Box
            sx={{
              position: "relative",
              overflow: "hidden",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              borderRadius: "12px",
              border: "1px solid rgba(224, 224, 224, 1)",
              minHeight: "657.9844px",
            }}
          >
            <Box
              component="img"
              src={imagePath}
              sx={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                filter: "blur(12px)",
                zIndex: 0,
              }}
            />

            <Box
              sx={{
                position: "absolute",
                inset: 0,
                background: "rgba(255,255,255,0.6)",
                zIndex: 1,
              }}
            />

            <Paper
              sx={{
                position: "relative",
                zIndex: 2,
                width: "100%",
                maxWidth: "571px",
                borderRadius: "8px",
                p: "32px 24px",
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                boxShadow:
                  "0 4px 20px rgba(0,0,0,0.10), 0 1px 3px rgba(0,0,0,0.08)",
              }}
            >
              <Box
                component="img"
                src={"/CkLock.svg"}
                sx={{
                  width: "48px",
                  height: "48px",
                  mb: 1,
                }}
              />

              {isSaasExperience && (
                <Typography
                  sx={{ fontSize: "34px", fontFamily: "Rethink Sans" }}
                  variant="h6"
                  fontWeight={600}
                  gutterBottom
                >
                  Upgrade to Unlock
                </Typography>
              )}
              {!isSaasExperience && (
                <Typography
                  sx={{ fontSize: "34px", fontFamily: "Rethink Sans" }}
                  variant="h6"
                  fontWeight={600}
                  gutterBottom
                >
                  Coming Soon
                </Typography>
              )}

              {!isSaasExperience && (
                <Typography
                  fontWeight={400}
                  sx={{ fontFamily: "Rethink Sans" }}
                  gutterBottom
                >
                  We’re currently working on this feature and are excited to
                  bring it to the platform soon. Our team is actively building
                  it out, and we’ll let you know as soon as it’s available.
                </Typography>
              )}

              {!hasBookedCallStep && isSaasExperience && (
                <Typography
                  sx={{ mb: 1, fontFamily: "Rethink Sans" }}
                  fontWeight={400}
                  gutterBottom
                >
                  You’ve discovered a premium feature.
                </Typography>
              )}
              {!hasBookedCallStep && isSaasExperience && (
                <Typography
                  fontWeight={400}
                  sx={{ fontFamily: "Rethink Sans" }}
                  gutterBottom
                >
                  We’re rolling this out to users who are ready to take full
                  advantage of everything the platform offers. Book a quick call
                  with us to unlock early access and see how it can work for
                  you.
                </Typography>
              )}
              {hasBookedCallStep && (
                <Typography
                  sx={{ mb: 1, fontFamily: "Rethink Sans" }}
                  fontWeight={400}
                  gutterBottom
                >
                  You’re all set. This feature might be activated during your
                  upcoming call, where we’ll walk you through how it works.
                </Typography>
              )}
              {hasBookedCallStep && isSaasExperience && (
                <Typography
                  fontWeight={400}
                  sx={{ fontFamily: "Rethink Sans" }}
                  gutterBottom
                >
                  You’re one step away from unlocking it.
                </Typography>
              )}
              {!hasBookedCallStep && isSaasExperience && (
                <PrimaryButton
                  sx={{ mt: "24px" }}
                  onClick={() => navigate("/performance")}
                >
                  Schedule A Call To Unlock
                </PrimaryButton>
              )}
            </Paper>
          </Box>
        </Box>
      </LayoutWrapper>
    </ThemeProvider>
  );
}
